import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AppLayout } from '../components/layout';
import { Button } from '@/components/ui/button';
import { Plus, ArrowLeft, Loader2, FileText, Folder, ChevronDown, BookOpen, ExternalLink, Settings, BarChart3 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  getHelpCenter,
  getHelpCenterStructure,
  createCategory,
  updateCategory,
  deleteCategory,
  reorderCategories,
  createItem,
  deleteItem,
  reorderItems,
} from '../services/api';
import type { HelpCenter, HelpCenterStructureNode, CreateCategoryRequest, UpdateCategoryRequest, CreateItemRequest, UpdateItemRequest } from '../types';
import { StructureTree } from '../components/features/help-center/structure/StructureTree';
import { CategoryModal } from '../components/features/help-center/CategoryModal';
import { AddGuideModal } from '../components/features/help-center/AddGuideModal';
import { EditGuideItemModal } from '../components/features/help-center/EditGuideItemModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useUIStore } from '../store/uiStore';
import { useBillingInfo, useIsProCloudPlan, useUserStore } from '../store/userStore';
import { useOpenHelpCenter } from '../hooks/useOpenHelpCenter';
import UpsellModal from '../components/modals/UpsellModal';
import { HelpCenterSettingsDrawer } from '../components/features/help-center/HelpCenterSettingsDrawer';
import { LimitReachedAlert } from '../components/features/portal/LimitReachedAlert';

const EditHelpCenterStructurePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openModal, closeModal, modals } = useUIStore();
  const isProCloudPlan = useIsProCloudPlan();
  const billingInfo = useBillingInfo();
  const { account } = useUserStore();
  const { openHelpCenter, openExternalUrl } = useOpenHelpCenter();

  const [helpCenter, setHelpCenter] = useState<HelpCenter | null>(null);
  const [structure, setStructure] = useState<HelpCenterStructureNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Store expanded category IDs to preserve across structure updates
  const expandedCategoryIdsRef = useRef<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any | null>(null);
  const [parentIdForNew, setParentIdForNew] = useState<number | null>(null);
  const [nodeToDelete, setNodeToDelete] = useState<{ type: 'category' | 'item'; data: any } | null>(null);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [showAddGuideModal, setShowAddGuideModal] = useState(false);
  const [editingGuideItem, setEditingGuideItem] = useState<{ helpCenterId: string; itemId: string } | null>(null);
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async (showLoading = true) => {
    if (!id) return;

    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const [helpCenterResponse, structureResponse] = await Promise.all([
        getHelpCenter(id),
        getHelpCenterStructure(id),
      ]);

      if (helpCenterResponse.ok && helpCenterResponse.helpCenter) {
        setHelpCenter(helpCenterResponse.helpCenter);
      } else {
        setError('Failed to load help center');
      }

      if (structureResponse.ok && structureResponse.structure) {
        setStructure(structureResponse.structure);
        // Expanded state will be preserved by StructureTree component via ref
      } else if (structureResponse.ok) {
        setStructure([]);
      } else {
        setError('Failed to load structure');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleAddCategory = (parentId: number | null) => {
    setEditingCategory(null);
    setParentIdForNew(parentId);
    setCategoryError(null);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    setParentIdForNew(null);
    setCategoryError(null);
    setShowCategoryModal(true);
  };

  const handleDeleteNode = (node: HelpCenterStructureNode) => {
    setNodeToDelete({ type: node.type, data: node.type === 'category' ? node.category : node.item });
    openModal('delete');
  };

  const handleConfirmDelete = async () => {
    if (!nodeToDelete || !id) return;

    closeModal('delete');
    setIsSubmitting(true);

    try {
      let response;
      if (nodeToDelete.type === 'category') {
        response = await deleteCategory(id, nodeToDelete.data.id.toString());
      } else {
        response = await deleteItem(id, nodeToDelete.data.id.toString());
      }
      
      if (response.ok) {
        toast.success(`${nodeToDelete.type === 'category' ? 'Category' : 'Item'} deleted successfully`);
        await fetchData();
        setNodeToDelete(null);
      } else {
        const errorMessage = response.errors && response.errors.length > 0
          ? response.errors[0]
          : response.error || `Failed to delete ${nodeToDelete.type}`;
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Error deleting:', err);
      toast.error(`Failed to delete ${nodeToDelete.type}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategorySubmit = async (data: CreateCategoryRequest | UpdateCategoryRequest) => {
    if (!id) return;

    setIsSubmitting(true);
    setCategoryError(null);

    try {
      let response;
      if (editingCategory) {
        response = await updateCategory(id, editingCategory.id.toString(), data as UpdateCategoryRequest);
      } else {
        response = await createCategory(id, data as CreateCategoryRequest);
      }

      if (response.ok && response.category) {
        toast.success(editingCategory ? 'Category updated successfully' : 'Category created successfully');
        setShowCategoryModal(false);
        setEditingCategory(null);
        setParentIdForNew(null);
        await fetchData();
      } else {
        const errorMessage = response.errors && response.errors.length > 0
          ? response.errors[0]
          : response.error || `Failed to ${editingCategory ? 'update' : 'create'} category`;
        setCategoryError(errorMessage);
      }
    } catch (err) {
      console.error('Error saving category:', err);
      setCategoryError('Failed to save category. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReorderCategories = async (updates: Array<{ category_id: number; parent_id: number | null; order: number }>) => {
    if (!id) return;

    try {
      const response = await reorderCategories(id, updates);
      if (response.ok) {
        // Silently refetch in background - don't show loading state
        fetchData(false).catch(err => {
          console.error('Error refetching after reorder:', err);
        });
      } else {
        toast.error('Failed to reorder categories');
        // Refetch to restore correct state
        await fetchData();
      }
    } catch (err) {
      console.error('Error reordering categories:', err);
      toast.error('Failed to reorder categories');
      // Refetch to restore correct state
      await fetchData();
    }
  };

  const handleReorderItems = async (updates: Array<{ item_id: number; category_id: number | null; order: number }>) => {
    if (!id) return;

    try {
      const response = await reorderItems(id, updates);
      if (response.ok) {
        // Silently refetch in background - don't show loading state
        fetchData(false).catch(err => {
          console.error('Error refetching after reorder:', err);
        });
      } else {
        toast.error('Failed to reorder items');
        // Refetch to restore correct state
        await fetchData();
      }
    } catch (err) {
      console.error('Error reordering items:', err);
      toast.error('Failed to reorder items');
      // Refetch to restore correct state
      await fetchData();
    }
  };

  const handleAddArticle = async (categoryId: number | null = null) => {
    if (!id) return;

    // Check item limits before attempting to add
    if (!isProCloudPlan) {
      const itemsLimit = billingInfo?.helpCenterItemsLimit ?? 20;
      const itemsCreated = billingInfo?.helpCenterItemsCreated ?? 0;
      if (itemsCreated >= itemsLimit) {
        toast.error(`Free plan allows only ${itemsLimit} items per help center. Upgrade to add more items.`);
        openModal('upsell');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Create item with article (item creation will create the article)
      const itemResponse = await createItem(id, {
        item_type: 'article',
        // Don't provide item_id - article will be created automatically
        category_id: categoryId ?? null,
        is_published: true,
        article_title: 'Untitled Article',
        article_description: '',
        article_content_text: '',
        article_settings: {
          showAuthor: true,
        },
      });

      if (!itemResponse.ok || !itemResponse.item) {
        const errorMsg = itemResponse.error || 'Failed to create item';
        // If error mentions limit or upgrade, show upsell modal
        if (errorMsg.toLowerCase().includes('limit') || errorMsg.toLowerCase().includes('upgrade')) {
          openModal('upsell');
        }
        throw new Error(errorMsg);
      }

      const item = itemResponse.item;

      // Navigate to edit page using item ID
      navigate(`/help-centers/${id}/items/${item.id}/edit`);
    } catch (err) {
      console.error('Error creating article:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create article');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditItem = (item: any) => {
    if (!id) return;
    
    if (item.item_type === 'article') {
      // Navigate to article editor
      navigate(`/help-centers/${id}/items/${item.id}/edit`);
    } else if (item.item_type === 'guide') {
      // Open guide item editor modal
      setEditingGuideItem({
        helpCenterId: id,
        itemId: item.id.toString(),
      });
    }
  };

  const [categoryIdForNewGuide, setCategoryIdForNewGuide] = useState<number | null>(null);

  const handleAddGuide = (categoryId: number | null = null) => {
    setCategoryIdForNewGuide(categoryId);
    setShowAddGuideModal(true);
  };

  const handleAddGuideToHelpCenter = async (guideIds: number[]) => {
    if (!id || guideIds.length === 0) return;

    // Check item limits before attempting to add
    if (!isProCloudPlan) {
      const itemsLimit = billingInfo?.helpCenterItemsLimit ?? 20;
      const itemsCreated = billingInfo?.helpCenterItemsCreated ?? 0;
      if (itemsCreated >= itemsLimit) {
        toast.error(`Free plan allows only ${itemsLimit} items per help center. Upgrade to add more items.`);
        openModal('upsell');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const promises = guideIds.map(guideId =>
        createItem(id, {
          item_type: 'guide',
          item_id: guideId,
          category_id: categoryIdForNewGuide ?? null,
          is_published: true,
        })
      );

      const responses = await Promise.all(promises);
      const successful = responses.filter(r => r.ok && r.item);
      const failed = responses.filter(r => !r.ok);

      if (successful.length > 0) {
        toast.success(
          `Successfully added ${successful.length} guide${successful.length > 1 ? 's' : ''} to help center`
        );
        setShowAddGuideModal(false);
        setCategoryIdForNewGuide(null);
        await fetchData();
      }

      if (failed.length > 0) {
        const errorMessage = failed[0].errors && failed[0].errors.length > 0
          ? failed[0].errors[0]
          : failed[0].error || 'Failed to add some guides';
        toast.error(errorMessage);
        // If error mentions limit or upgrade, show upsell modal
        if (errorMessage.toLowerCase().includes('limit') || errorMessage.toLowerCase().includes('upgrade')) {
          openModal('upsell');
        }
      }
    } catch (err) {
      console.error('Error adding guides:', err);
      toast.error('Failed to add guides');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpgradeClick = () => {
    const url =
      "https://folge.me/go/cloud?cloudemail=" +
      encodeURIComponent(account?.email || "") +
      "#pricing";
    openExternalUrl(url);
  };

  const breadcrumbs = [
    { title: 'Dashboard', href: '/dashboard' },
    { title: 'Help Centers', href: '/help-centers' },
    { title: helpCenter?.title || 'Edit Structure', isCurrentPage: true },
  ];

  if (loading) {
    return (
      <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (error || !helpCenter) {
    return (
      <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
        <div className="p-4">
          <div className="text-destructive">{error || 'Help center not found'}</div>
          <Button onClick={() => navigate('/help-centers')} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Help Centers
          </Button>
        </div>
      </AppLayout>
    );
  }

  const itemsLimit = billingInfo?.helpCenterItemsLimit ?? 20;
  const itemsCreated = billingInfo?.helpCenterItemsCreated ?? 0;
  const isAtItemLimit = !isProCloudPlan && itemsCreated >= itemsLimit;

  return (
    <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
      <UpsellModal
        isOpen={modals.upsell}
        onClose={() => closeModal("upsell")}
      />
      <div className="p-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-2xl font-bold">{helpCenter.title}             {helpCenter.slug && (
              <button
                onClick={() => openHelpCenter(helpCenter.slug)}
                className="text-muted-foreground hover:text-foreground transition-colors p-1"
                title="Open help center"
              >
                <ExternalLink className="h-5 w-5" />
              </button>
            )}</h1>
              <p className="text-muted-foreground mt-1">Manage categories and structure</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/help-centers')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/reports?helpCenterId=${id}`)}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Reports
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowSettingsDrawer(true)}
            >
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button disabled={isAtItemLimit}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleAddArticle(null)} disabled={isAtItemLimit}>
                  <FileText className="h-4 w-4 mr-2" />
                  Add Article
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleAddGuide(null)} disabled={isAtItemLimit}>
                  <BookOpen className="h-4 w-4 mr-2" />
                  Add Guide
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleAddCategory(null)}>
                  <Folder className="h-4 w-4 mr-2" />
                  Add Category
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {!isProCloudPlan && (
          <div className="mb-4">
            <LimitReachedAlert
              message={`${itemsCreated} / ${itemsLimit} items${isAtItemLimit ? ' (limit reached)' : ''}`}
              onUpgradeClick={handleUpgradeClick}
            />
          </div>
        )}

        <div className="bg-card border rounded-lg p-4 min-h-[500px]">
          {structure.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No structure yet</p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center">
                  <DropdownMenuItem onClick={() => handleAddArticle(null)}>
                    <FileText className="h-4 w-4 mr-2" />
                    Add Article
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAddGuide(null)}>
                    <BookOpen className="h-4 w-4 mr-2" />
                    Add Guide
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAddCategory(null)}>
                    <Folder className="h-4 w-4 mr-2" />
                    Add Category
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <StructureTree
              structure={structure}
              onReorderCategories={handleReorderCategories}
              onReorderItems={handleReorderItems}
              onAddCategory={handleAddCategory}
              onEditCategory={handleEditCategory}
              onDeleteNode={handleDeleteNode}
              onAddArticle={handleAddArticle}
              onAddGuide={handleAddGuide}
              onEditItem={handleEditItem}
              helpCenterId={helpCenter.id}
              helpCenterSlug={helpCenter.slug}
              expandedCategoryIdsRef={expandedCategoryIdsRef}
            />
          )}
        </div>
      </div>

      <CategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
          setParentIdForNew(null);
          setCategoryError(null);
        }}
        onSubmit={handleCategorySubmit}
        category={editingCategory}
        parentId={parentIdForNew}
        helpCenterId={helpCenter.id}
        isSubmitting={isSubmitting}
        error={categoryError}
      />

      <AddGuideModal
        isOpen={showAddGuideModal}
        onClose={() => {
          setShowAddGuideModal(false);
          setCategoryIdForNewGuide(null);
        }}
        onAdd={handleAddGuideToHelpCenter}
        helpCenterId={helpCenter.id}
        categoryId={categoryIdForNewGuide}
        isSubmitting={isSubmitting}
      />

      {editingGuideItem && (
        <EditGuideItemModal
          isOpen={!!editingGuideItem}
          onClose={() => setEditingGuideItem(null)}
          helpCenterId={editingGuideItem.helpCenterId}
          itemId={editingGuideItem.itemId}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}

      <Dialog open={modals.delete} onOpenChange={(open) => {
        if (!open) {
          closeModal('delete');
          setNodeToDelete(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete "{nodeToDelete?.type === 'category' ? nodeToDelete.data?.title : (nodeToDelete?.data?.article?.title || nodeToDelete?.data?.guide?.title || 'this item')}"? This action cannot be undone.
              {nodeToDelete?.type === 'category' && nodeToDelete.data?.children && nodeToDelete.data.children.length > 0 && (
                <span className="block mt-2 text-destructive">
                  Warning: This category has {nodeToDelete.data.children.length} subcategor{nodeToDelete.data.children.length === 1 ? 'y' : 'ies'} that will also be deleted.
                </span>
              )}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                closeModal('delete');
                setNodeToDelete(null);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HelpCenterSettingsDrawer
        isOpen={showSettingsDrawer}
        onClose={() => setShowSettingsDrawer(false)}
        helpCenterId={id || null}
        onSuccess={() => {
          fetchData();
        }}
      />
    </AppLayout>
  );
};

export default EditHelpCenterStructurePage;
